<template>
  <UiComponentWrapper class="page">
    <!-- Section > Hero -->
    <UiComponentContainer class="inner">
      <OrganismHero>
        <template #tag>
          {{ data.page.title }}
        </template>
        <template #heading>
          {{ data.page.subtitle }}
        </template>
      </OrganismHero>
      <UiComponentImage
        :key="imageFullWidth"
        :alt="data.page.title"
        :expected-size="imageFullWidth"
        :height="data.page.aboutCover.height"
        :lazyload="false"
        :orientation="
          data.page.aboutCover.width > data.page.aboutCover.height
            ? 'landscape'
            : 'portrait'
        "
        :source="data.page.aboutCover.url"
        :width="data.page.aboutCover.width"
      />
    </UiComponentContainer>

    <!-- Section > Our Story -->
    <UiComponentContainer class="inner constraint">
      <OrganismHero
        alignment="left"
        class="content-header"
        :level="2"
      >
        <template #tag>
          {{ data.page.storyTitle }}
        </template>
        <template #heading>
          {{ data.page.ourStoryTitle }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="content-split">
        <UiComponentParagraph>{{ data.page.ourStoryFirstText }}</UiComponentParagraph>
        <UiComponentParagraph>{{ data.page.ourStorySecondText }}</UiComponentParagraph>
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Timeline -->
    <UiComponentContainer class="inner history">
      <UiComponentWrapper class="timeline">
        <UiComponentWrapper
          v-for="item in data.page.timeline"
          :key="item.id"
          class="item"
        >
          <UiComponentWrapper class="dot" />
          <UiComponentWrapper class="event">
            <UiComponentHeading :level="3">
              {{ item.title }}
            </UiComponentHeading>
            <UiComponentParagraph>{{ item.month }} {{ item.year }}.</UiComponentParagraph>
          </UiComponentWrapper>
          <UiComponentParagraph class="description">
            {{ item.text }}
          </UiComponentParagraph>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentImage
        :alt="data.page.storyPicture.alt || 'timeline'"
        :expected-size="imageWidth"
        :height="data.page.storyPicture.height"
        :source="data.page.storyPicture.url"
        :width="data.page.storyPicture.width"
      />
    </UiComponentContainer>

    <!-- Section > Video engagement platform -->
    <UiComponentContainer class="inner constraint">
      <OrganismHero
        class="content-header"
        :level="2"
      >
        <template #heading>
          {{ data.page.ourStorySecondTitle }}
        </template>
        <template #paragraph>
          {{ data.page.ourStorySecondSubtitle }}
        </template>
      </OrganismHero>
    </UiComponentContainer>

    <!-- Section > Testimonial -->
    <MoleculeCustomerQuote
      class="py-20 bg-grey-blue-100 mt-28 xl:mt-48"
      :quote="quote"
    />

    <!-- Section > Our values -->
    <UiComponentContainer class="section inner">
      <UiComponentHeading :level="2">
        {{ data.page.valuesTitle }}
      </UiComponentHeading>
      <UiComponentWrapper class="values">
        <MoleculeFeature
          v-for="item in values"
          :key="item.id"
          direction="column"
          :feature="item"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Join the team -->
    <UiComponentContainer class="section team inner">
      <OrganismHero
        class="content-header"
        :level="2"
        :primary-button="{
          action: 'Button clicked',
          attributes: {
            action: data.page.teamLink,
          },
          label: 'Team section CTA',
          slot: data.page.teamCta,
          to: data.page.teamLink,
        }"
      >
        <template #heading>
          {{ data.page.teamTitle }}
        </template>
        <template #paragraph>
          {{ data.page.teamText }}
        </template>
      </OrganismHero>
    </UiComponentContainer>
    <UiComponentContainer class="glide slider">
      <div
        class="w-full glide__track"
        data-glide-el="track"
      >
        <div class="glide__slides">
          <div
            v-for="image in data.page.teamSlider"
            :key="image.id"
            class="glide__slide"
          >
            <img
              :alt="image.alt"
              class="rounded-4xl"
              :height="400"
              loading="lazy"
              :src="`${image.url}?auto=format&w=400&h=400&fit=crop`"
              :srcset="`${image.url}?auto=format&w=400&h=400&fit=crop&dpr=1 1x, ${image.url}?auto=format&w=400&h=400&fit=crop&q=40&dpr=2 2x, ${image.url}?auto=format&w=400&h=400&fit=crop&q=20&dpr=3 3x`"
              :width="400"
            >
          </div>
        </div>
      </div>
    </UiComponentContainer>

    <!-- Section > Our customers -->
    <UiComponentContainer class="inner customers">
      <OrganismHero
        class="content-header"
        :level="2"
        :primary-button="{
          action: 'Button clicked',
          attributes: {
            action: localePath({ name: 'customers' }),
          },
          label: 'Customer section CTA',
          slot: data.page.clientTitle,
          to: localePath({ name: 'customers' }),
        }"
      >
        <template #heading>
          {{ data.page.clientTitle }}
        </template>
        <template #paragraph>
          {{ data.page.clientText }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="logos">
        <UiComponentImage
          v-for="image in customers"
          :key="image.id"
          :alt="image.alt"
          class="logo"
          contains
          :expected-size="48"
          :height="image.height"
          orientation="portrait"
          :source="image.url"
          :width="image.width"
        />
      </UiComponentWrapper>
    </UiComponentContainer>

    <!-- Section > Press -->
    <UiComponentWrapper class="section inner press">
      <UiComponentContainer class="flex-col">
        <UiComponentWrapper class="flex-row pt-20">
          <OrganismHero
            alignment="left"
            class="self-center mt-0"
            :level="2"
            :primary-button="{
              action: 'Button clicked',
              attributes: {
                action: data.page.pressLink,
              },
              label: 'Press section CTA',
              slot: data.page.pressCta,
              to: data.page.pressLink,
            }"
          >
            <template #tag>
              {{ data.page.pressTag }}
            </template>
            <template #heading>
              {{ data.page.pressTitle }}
            </template>
            <template #paragraph>
              {{ data.page.pressText }}
            </template>
          </OrganismHero>
          <MoleculeContactCard>
            <template #title>
              {{ data.page.contactTitle }}
            </template>
            <template #paragraph>
              {{ data.page.contactText }}
            </template>
            <template #call-to-action>
              {{ data.page.contactCta }}
            </template>
          </MoleculeContactCard>
        </UiComponentWrapper>
        <UiComponentWrapper class="articles">
          <MoleculeArticleCard
            v-for="article in articles"
            :key="article.id"
            :article="article"
          />
        </UiComponentWrapper>
      </UiComponentContainer>
    </UiComponentWrapper>

    <!-- Section > Investors -->
    <UiComponentWrapper class="inner investors">
      <OrganismHero
        class="content-header"
        :level="2"
      >
        <template #tag>
          {{ data.page.investorTag }}
        </template>
        <template #heading>
          {{ data.page.investorTitle }}
        </template>
      </OrganismHero>
      <UiComponentWrapper class="logos">
        <UiComponentImage
          v-for="image in data.page.investorImages"
          :key="image.id"
          :alt="image.alt || 'logo'"
          class="logo"
          contains
          :expected-size="48"
          :height="image.height"
          orientation="portrait"
          :source="image.url"
          :width="image.width"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
</template>

<script setup>
import Glide from '@glidejs/glide'

// Layout
definePageMeta({
  layout: 'default',
})

// i18n
defineI18nRoute({
  paths: {
    de: '/ueber-uns',
    en: '/about',
    es: '/sobre-nosotros',
    fr: '/a-propos',
  },
})

// Composables
const { page } = useAnalyticsJS()
const { setAvailableLanguages } = useAvailableLanguages()
const { updateListItems } = useBreadcrumb()
const { state } = useConditionalPreviewMode()
const { locale } = useI18n()
const localePath = useLocalePath()
const getRouteBaseName = useRouteBaseName()
const { public: publicRuntimeConfig } = useRuntimeConfig()
const { width: screenWidth } = useScreenSize()

// Async Data
const { data } = await useFetch(`/api/content/pages/about/${locale.value}`, {
  key: `page-about-${locale.value}`,
  query: {
    preview: state.preview,
  },
})

// Meta
const head = useLocaleHead({
  identifierAttribute: 'id',
  addSeoAttributes: true,
})

useSeoMeta({
  title: data.value.page.titleSeo,
  description: data.value.page.descriptionSeo,
  ogTitle: data.value.page.titleSeo,
  ogDescription: data.value.page.descriptionSeo,
  twitterTitle: data.value.page.titleSeo,
  twitterDescription: data.value.page.descriptionSeo,
})

useHead({
  link: head.value.link,
  meta: head.value.meta,
  script: [
    {
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': [
          {
            '@type': 'ListItem',
            'position': 1,
            'name': data.value.home.trackingBreadcrumbTitle,
            'item': `${publicRuntimeConfig.baseUrl}${localePath('index')}`,
          },
          {
            '@type': 'ListItem',
            'position': 2,
            'name': data.value.page.trackingBreadcrumbTitle,
          },
        ],
      }),
      type: 'application/ld+json',
    },
  ],
})

// Refs
const glide = ref(null)

// Computed Properties
const articles = computed(() => {
  return data.value.page.v2Articles.map(article => ({
    id: article.id,
    backgroundColor: article.pressColor,
    callToAction: data.value.page.readMore,
    logo: {
      ...article.pressLogo,
      alt: article.pressLogo.alt || article.pressText,
    },
    title: article.pressText,
    to: article.pressLink,
  }))
})

const customers = computed(() => {
  return data.value.page.customers.slice(0, 3).map(customer => ({
    id: customer.id,
    alt: customer.name,
    ...customer.logoColor,
  }))
})

const imageFullWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 48
    default:
      return 1280
  }
})

const imageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return screenWidth.value - 48
    default:
      return 620
  }
})

const quote = computed(() => {
  return {
    content: data.value.page?.citation,
    customer: {
      avatar: {
        alt: `${data.value.page?.authorName}' Avatar`,
        height: data.value.page?.authorImage?.height,
        src: data.value.page?.authorImage?.url,
        width: data.value.page?.authorImage?.width,
      },
      name: data.value.page?.authorName,
      position: data.value.page?.authorPosition,
    },
    logo: {
      alt: 'Livestorm\' logo',
      height: data.value.page.citationLogo.height,
      src: data.value.page.citationLogo.url,
      width: data.value.page.citationLogo.width,
    },
  }
})

const values = computed(() => {
  return data.value.page.valuesV2.map(value => ({
    id: value.id,
    description: value.text,
    icon: value.icon,
    title: value.title,
  }))
})

// Lifecycle Hooks
onBeforeUnmount(() => {
  glide.value.destroy()
})

onMounted(() => {
  glide.value = new Glide('.glide', {
    type: 'slider',
    bound: true,
    gap: 40,
    peek: {
      before: 0,
      after: 0,
    },
    perView: 3,
    rewind: false,
    breakpoints: {
      1024: {
        peek: {
          before: 24,
          after: 24,
        },
        perView: 2,
      },
      768: {
        peek: {
          before: 16,
          after: 16,
        },
        perView: 1,
      },
    },
  })
  glide.value.mount()

  page(data.value.page.trackName || 'About')
  setAvailableLanguages()
  updateListItems([
    {
      item: localePath('index'),
      name: data.value.home.trackingBreadcrumbTitle,
    },
    {
      item: localePath({ name: getRouteBaseName() }),
      name: data.value.page.trackingBreadcrumbTitle,
    },
  ])
})
</script>

<style lang="postcss" scoped>
@import "../assets/css/glide.css";

.page {
  @apply flex-col items-center;

  .inner {
    @apply flex-col;

    &.section {
      @apply mt-20;

      @screen mdminus {
        @apply mt-16;
      }

      @screen mdplus {
        @apply mt-26;
      }
    }

    &.constraint {
      @apply max-w-5xl;
    }

    &.history {
      @apply flex-col mt-20;

      @screen mdminus {
        @apply mt-16;
      }

      @screen mdplus {
        @apply mt-26;
      }

      @screen xl {
        @apply flex-row-reverse;
      }

      .timeline {
        @screen xl {
          @apply ml-10;
          width: 620px;
        }
      }

      picture {
        @screen xl {
          width: 620px;
        }
      }
    }

    &.press {
      @apply flex-col items-center bg-livestorm-blue-100 pb-20;

      :deep(.tag) {
        @apply bg-white text-livestorm-blue-700;
      }
    }

    .hero {
      @apply py-8;

      @screen md {
        @apply py-16;
      }

      @screen lg {
        @apply py-20;
      }

      @screen xxxl {
        @apply py-26;
      }
    }

    .content-header {
      @apply mt-20 py-0;

      @screen mdminus {
        @apply mt-16;
      }

      @screen mdplus {
        @apply mt-26;
      }
    }

    .content-split {
      @apply mt-10 flex-col;

      @screen md {
        @apply flex-row;
      }

      p {
        @apply text-grey-blue-700 w-full;

        &:first-child {
          @screen md {
            @apply pr-6;
          }
        }

        &:last-child {
          @apply mt-8;

          @screen md {
            @apply mt-0 pl-6;
          }
        }
      }
    }

    .timeline {
      @apply flex-col;

      @screen xl {
        @apply pl-12;
      }

      .item {
        @apply relative flex-col pb-8 pl-4 border-l border-grey-blue-200;

        &:last-child {
          @apply border-l-0;
        }

        @screen xl {
          @apply flex-row;
        }

        .dot {
          @apply absolute top-0 left-0 block h-3 w-3 bg-grey-blue-300 rounded-full border-2 border-white;
          transform: translateX(-50%) translateY(50%);
        }

        .event {
          @apply flex-col justify-start w-full;

          @screen xl {
            @apply w-48;
          }

          .h3 {
            @apply text-origin font-medium;
          }

          p {
            @apply mt-0 text-grey-blue-600;
          }
        }

        .description {
          @apply text-grey-blue-600;

          @screen xl {
            width: calc(100% - 12rem);
          }
        }
      }
    }

    .values {
      @apply flex-col gap-10 mt-20;

      @screen xl {
        @apply flex-row flex-wrap justify-between gap-0;
      }

      .column {
        @apply flex-row;

        @screen xl {
          @apply flex-col max-w-[240px];
        }

        :deep(.body) {
          @apply mt-0 ml-6;

          @screen xl {
            @apply mt-8 ml-0;
          }
        }
      }
    }

    .articles {
      @apply flex-col mt-20;

      @screen mdminus {
        @apply mt-16;
      }

      @screen mdplus {
        @apply mt-26;
      }

      @screen lg {
        @apply flex-row;
      }

      .article-card {
        @apply mt-10;

        &:first-child {
          @apply mt-0;
        }

        @screen lg {
          @apply mt-0;
          width: calc(calc(100% - 5rem) / 3);

          &:nth-child(1) {
            @apply ml-0 mr-5;
          }

          &:nth-child(2) {
            @apply ml-5 mr-5;
          }

          &:nth-child(3) {
            @apply ml-5 mr-0;
          }
        }
      }
    }

    .logos {
      @apply h-auto justify-center mt-12;

      .logo {
        @apply h-12 mx-4 rounded-none;

        &:first-child {
          @apply ml-0;
        }

        &:last-child {
          @apply mr-0;
        }
      }
    }
  }

  .slider {
    @apply mt-10;

    img {
      @apply rounded-4xl;
    }
  }
}
</style>
